import * as React from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import moment from "moment";
import { TempRequestsListProps, TempRequest } from "../types";
import TempRatingModal from "../../ratings/components/TempRatingModal";

// Helper to get status based on temp request state
const getRequestStatus = (row: TempRequest): string => {
  // Check cancelled first (highest priority)
  if (row.cancelled) return "Cancelled";

  if (row.accepted_by_professional === false) return "Declined";
  if (row.accepted_by_professional && row.accepted_by_practice) return "Accepted";
  if (row.accepted_by_professional === null) return "Pending";

  // Any other state should show as Cancelled
  return "Cancelled";
};

// Column definitions
const createColumns = (
  type: 'pending' | 'accepted' | 'declined' | 'all',
  onOpenRatingModal: (ratingId: number) => void,
  onAccept?: (id: number) => void,
  onDecline?: (id: number) => void
): TableColumn<TempRequest>[] => {
  const columns: TableColumn<TempRequest>[] = [
    {
      name: "Practice",
      selector: (row) => row.job.practice.name,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img
            src={row.job.practice.avatar_url}
            alt={row.job.practice.name}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid #e8ecef'
            }}
          />
          <a
            href={`/practices/${row.job.practice.id}`}
            style={{
              color: '#085ec8',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {row.job.practice.name}
          </a>
        </div>
      ),
      grow: 2,
    },
    {
      name: "Position",
      selector: (row) => row.job.job_type.title,
      sortable: true,
      cell: (row) => (
        <a
          href={`/jobs/${row.job.id}`}
          style={{
            fontWeight: 500,
            color: '#085ec8',
            textDecoration: 'none',
            transition: 'color 0.3s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
          onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
        >
          {row.job.job_type.title}
        </a>
      ),
      grow: 1.5,
    },
    {
      name: "Date",
      selector: (row) => new Date(row.job.temp_date).getTime(),
      sortable: true,
      cell: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {moment(row.job.temp_date).format("ll")}
        </span>
      ),
      grow: 1,
    },
    {
      name: "Time",
      selector: (row) => row.job.temp_start,
      sortable: false,
      cell: (row) => (
        <span style={{ whiteSpace: 'nowrap', fontSize: '0.9rem', color: '#555' }}>
          {row.job.temp_start_readable} - {row.job.temp_end_readable}
        </span>
      ),
      grow: 1.2,
    },
    {
      // Pay comes from the server already formatted the way job listings
      // show it, and blank when the temp job lists none.
      name: "Pay",
      selector: (row) => row.job.candidate_pay_text || "",
      sortable: false,
      cell: (row) => (
        <span style={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#333' }}>
          {row.job.candidate_pay_text || ""}
        </span>
      ),
      grow: 1.2,
    },
    {
      name: "Directions",
      sortable: false,
      cell: (row) => {
        const practice = row.job.practice;
        const streetAddress = [practice.street_address, practice.street_address_2].filter(Boolean).join(' ');
        const fullAddress = [streetAddress, practice.city, practice.state, practice.zip].filter(Boolean).join(', ');
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
        return (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.75rem',
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#0f4b89',
              backgroundColor: 'white',
              border: '1px solid #0f4b89',
              borderRadius: '0.375rem',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#0f4b89';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.color = '#0f4b89';
            }}
          >
            <i className="fa fa-directions"></i>
            Directions
          </a>
        );
      },
      grow: 1,
      minWidth: '120px',
    },
    {
      name: "Status",
      selector: (row) => {
        // Prioritize pending rating for sorting
        if (row.pending_rating?.needs_rating) return 0;
        if (row.accepted_by_professional && row.accepted_by_practice) return 1;
        if (row.accepted_by_professional === false) return 3;
        return 2;
      },
      sortable: true,
      cell: (row) => {
        // Show blue "Rated" button if rating is completed
        if (row.pending_rating && !row.pending_rating.needs_rating && row.pending_rating.rating_value) {
          const solidStars = '★'.repeat(row.pending_rating.rating_value);
          const outlinedStars = '☆'.repeat(5 - row.pending_rating.rating_value);
          const stars = solidStars + outlinedStars;
          return (
            <button
              type="button"
              onClick={() => onOpenRatingModal(row.pending_rating!.rating_id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                color: 'white',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(8, 94, 200, 0.25)',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.35)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(8, 94, 200, 0.25)';
              }}
            >
              <span>{stars}</span>
            </button>
          );
        }

        // Show golden "Rate Me" button if there's a pending rating
        if (row.pending_rating?.needs_rating) {
          return (
            <button
              type="button"
              onClick={() => onOpenRatingModal(row.pending_rating!.rating_id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #ffc107, #ffb300)',
                color: 'white',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 193, 7, 0.25)',
                transition: 'all 0.3s ease',
                animation: 'pulse-scale 2s ease-in-out infinite',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.35)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 193, 7, 0.25)';
              }}
            >
              <i className="fa fa-star"></i>
              <span>Rate Me</span>
            </button>
          );
        }

        // Default status badge
        const status = getRequestStatus(row);
        let badgeClass = 'badge-modern';
        let customStyle: React.CSSProperties = { whiteSpace: 'nowrap' };

        if (status === 'Accepted') {
          badgeClass += ' badge-modern--success';
        } else if (status === 'Pending') {
          badgeClass += ' badge-modern--warning';
        } else if (status === 'Declined' || status === 'Cancelled') {
          // Use inline styles for red badges
          customStyle = {
            ...customStyle,
            background: 'linear-gradient(135deg, #dc3545, #b02a37)',
            color: 'white'
          };
        } else {
          badgeClass += ' badge-modern--light';
        }

        return <span className={badgeClass} style={customStyle}>{status}</span>;
      },
      grow: 1.2,
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row) => {
        const isPending = row.accepted_by_professional === null && !row.cancelled;

        return (
          <div className="d-flex justify-content-center" style={{ gap: '0.5rem' }}>
            <a
              href={row.job_url}
              className="btn-modern btn-modern--primary btn-modern--sm"
              style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
            >
              <i className="fa fa-eye"></i> View Details
            </a>
            {isPending && (type === 'pending' || type === 'all') && (
              <>
                <button
                  onClick={() => onAccept && onAccept(row.id)}
                  className="btn-modern btn-modern--success btn-modern--sm"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className="fa fa-check"></i> Accept
                </button>
                <button
                  onClick={() => onDecline && onDecline(row.id)}
                  className="btn-modern btn-modern--danger btn-modern--sm"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className="fa fa-times"></i> Decline
                </button>
              </>
            )}
          </div>
        );
      },
      grow: 2,
    },
  ];

  return columns;
};

const TempRequestsList: React.FC<TempRequestsListProps> = ({
  tempRequests,
  type,
  authenticity_token
}) => {
  // Local state for optimistic updates
  const [requests, setRequests] = React.useState<TempRequest[]>(tempRequests);

  // Sync with prop changes (for filtering)
  React.useEffect(() => {
    setRequests(tempRequests);
  }, [tempRequests]);

  // Decline confirmation state
  const [declineConfirmation, setDeclineConfirmation] = React.useState<{
    show: boolean;
    practiceName: string;
    jobDate: string;
  } | null>(null);

  // Rating modal state
  const [ratingModalOpen, setRatingModalOpen] = React.useState(false);
  const [selectedRatingId, setSelectedRatingId] = React.useState<number | null>(null);
  const [selectedRequestId, setSelectedRequestId] = React.useState<number | null>(null);

  // Rating modal handlers
  const handleOpenRatingModal = (ratingId: number) => {
    // Find the request that has this rating
    const request = requests.find(r => r.pending_rating?.rating_id === ratingId);
    setSelectedRequestId(request?.id || null);
    setSelectedRatingId(ratingId);
    setRatingModalOpen(true);
  };

  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setSelectedRatingId(null);
    setSelectedRequestId(null);
  };

  const handleRatingSuccess = () => {
    // Optimistically update the rating in local state
    // Don't close modal - user may want to add comments
    if (selectedRequestId) {
      setRequests(prev => prev.map(req => {
        if (req.id === selectedRequestId && req.pending_rating) {
          return {
            ...req,
            pending_rating: {
              ...req.pending_rating,
              needs_rating: false,
              // We don't know the exact rating value, so we'll use a placeholder
              // The modal will show the actual stars when clicked
              rating_value: req.pending_rating.rating_value || 5
            }
          };
        }
        return req;
      }));
    }
  };

  // Action handlers
  const handleAccept = async (id: number) => {
    const request = requests.find(r => r.id === id);
    if (!request) return;

    if (confirm(`Accept this temp job at ${request.job.practice.name} on ${request.job.temp_date_readable}?`)) {
      // Optimistically remove from pending list
      setRequests(prev => prev.filter(r => r.id !== id));

      try {
        const response = await fetch(`/temp_requests/${id}/accept.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': authenticity_token
          }
        });

        if (response.ok) {
          // Redirect to the temp request detail page to show confirmation
          window.location.href = `/temp_requests/${id}`;
        } else {
          // Restore the request on error
          setRequests(prev => [...prev, request]);
          const error = await response.json();
          alert(`Failed to accept: ${error.error || 'Unknown error'}`);
        }
      } catch (error) {
        // Restore the request on error
        setRequests(prev => [...prev, request]);
        console.error('Accept error:', error);
        alert('Failed to accept the job. Please try again.');
      }
    }
  };

  const handleDecline = async (id: number) => {
    const request = requests.find(r => r.id === id);
    if (!request) return;

    if (confirm(`Decline this temp job at ${request.job.practice.name}?`)) {
      // Optimistically update status to declined (keep in list)
      setRequests(prev => prev.map(r =>
        r.id === id
          ? { ...r, accepted_by_professional: false }
          : r
      ));

      try {
        const response = await fetch(`/temp_requests/${id}/decline.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': authenticity_token
          }
        });

        if (response.ok) {
          // Show confirmation message
          setDeclineConfirmation({
            show: true,
            practiceName: request.job.practice.name,
            jobDate: request.job.temp_date_readable || request.job.temp_date
          });
          // Auto-hide after 5 seconds
          setTimeout(() => setDeclineConfirmation(null), 5000);
        } else {
          // Restore the request's original status on error
          setRequests(prev => prev.map(r =>
            r.id === id
              ? { ...r, accepted_by_professional: request.accepted_by_professional }
              : r
          ));
          const error = await response.json();
          alert(`Failed to decline: ${error.error || 'Unknown error'}`);
        }
      } catch (error) {
        // Restore the request's original status on error
        setRequests(prev => prev.map(r =>
          r.id === id
            ? { ...r, accepted_by_professional: request.accepted_by_professional }
            : r
        ));
        console.error('Decline error:', error);
        alert('Failed to decline the job. Please try again.');
      }
    }
  };

  const columns = createColumns(
    type,
    handleOpenRatingModal,
    handleAccept,
    handleDecline
  );

  // Empty state
  if (requests.length === 0) {
    const getEmptyMessage = () => {
      switch (type) {
        case 'pending':
          return {
            title: 'No Pending Requests',
            message: 'You have no pending temp requests at this time.'
          };
        case 'accepted':
          return {
            title: 'No Accepted Jobs',
            message: 'You have no accepted temp jobs at this time.'
          };
        case 'declined':
          return {
            title: 'No Declined Requests',
            message: 'You have no declined temp requests.'
          };
        default:
          return {
            title: 'No Requests',
            message: 'No temp requests found.'
          };
      }
    };

    const empty = getEmptyMessage();

    return (
      <div className="padding-right p-2">
        <div
          style={{
            background: 'white',
            border: '1px solid #e8ecef',
            borderRadius: '0.75rem',
            padding: '3rem 1.5rem',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
          }}
        >
          <img
            src="/images/missing.svg"
            alt=""
            height={100}
            style={{ marginBottom: '1rem' }}
          />
          <h4 style={{ color: '#333', marginBottom: '0.5rem', fontWeight: 600 }}>
            {empty.title}
          </h4>
          <p style={{ color: '#6c757d', marginBottom: 0 }}>{empty.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="padding-right">
      {/* Decline confirmation alert */}
      {declineConfirmation && (
        <div
          style={{
            background: 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
            border: '1px solid #f5c6cb',
            borderRadius: '0.75rem',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(220, 53, 69, 0.15)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc3545, #b02a37)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.25rem'
              }}
            >
              <i className="fa fa-check"></i>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#721c24', fontSize: '1rem' }}>
                Response Submitted
              </div>
              <div style={{ color: '#721c24', fontSize: '0.875rem' }}>
                You have declined the temp request at <strong>{declineConfirmation.practiceName}</strong> on {declineConfirmation.jobDate}
              </div>
            </div>
          </div>
          <button
            onClick={() => setDeclineConfirmation(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#721c24',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      )}

      {/* Mobile scroll hint */}
      <div
        className="d-md-none"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.5rem',
          marginBottom: '0.5rem',
          background: '#f8f9fa',
          borderRadius: '0.5rem',
          fontSize: '0.8rem',
          color: '#6c757d'
        }}
      >
        <i className="fa fa-arrows-alt-h"></i>
        <span>Swipe to see more</span>
      </div>
      <div
        style={{
          background: 'white',
          border: '1px solid #e8ecef',
          borderRadius: '0.75rem',
          padding: '0.75rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          transition: 'box-shadow 0.3s ease',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <DataTable
          data={requests}
          columns={columns}
          responsive
          customStyles={{
            responsiveWrapper: {
              style: {
                overflowX: 'visible',
                overflowY: 'visible'
              }
            },
            table: {
              style: {
                width: '100%',
                tableLayout: 'fixed'
              }
            },
            headCells: {
              style: {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#6c757d',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem'
              }
            },
            cells: {
              style: {
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                overflow: 'visible'
              }
            }
          }}
        />
      </div>

      {/* Temp Rating Modal */}
      {selectedRatingId && (
        <TempRatingModal
          ratingId={selectedRatingId}
          show={ratingModalOpen}
          onHide={handleCloseRatingModal}
          onSuccess={handleRatingSuccess}
        />
      )}
    </div>
  );
};

export default TempRequestsList;
